/* eslint-disable no-console */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment'; // NOSONAR: This import is necessary for moment usage. Default import doesn't work.
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfiguration } from '../../../core/models/app-configuration.model';
import { TwoFADevice } from './model/device.model';
import { TwoFactorPairingResponse, TwoFactorPairingStatus } from './model/pairing.model';
import { ContactRequest } from 'app/shared/model/contacts.model';

/**
 * Angular Service
 * @description Implements methods for 2fa authorization
 */
@Injectable({ providedIn: 'root' })
export class TwoStepVerificationService {
  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfiguration) { }

  getResourceUrl(): string {
    return this.appConfig.serverApiUrl + '/api/v1/twofactor/app';
  }

  /**
   * Start pairing for a new device
   * @param authReqId Authorization request id
   */
  pairingDevice(authReqId: string): Observable<TwoFactorPairingResponse> {
    return this.http
      .put<TwoFactorPairingResponse>(this.getResourceUrl() + '/pairing', null, {
        headers: new HttpHeaders({ authPermission: authReqId }),
      })
      .pipe(map((res: TwoFactorPairingResponse) => this.convertDateFromServer(res, 'expirationDate')));
  }

  /**
   * Retrieve info about progress of a specific pairing id
   * @param pairingId current pairing id
   * @param authReqId authorization request id
   */
  getPairingDeviceStatus(pairingId: string, authReqId: string): Observable<TwoFactorPairingStatus> {
    const options = { headers: new HttpHeaders({ authPermission: authReqId }) };
    return this.http
      .get<TwoFactorPairingStatus>(`${this.getResourceUrl()}/pairing/${pairingId}`, options)
      .pipe(map((res: TwoFactorPairingStatus) => this.convertDateFromServer(res, 'nextRequestNotBefore')));
  }

  /**
   * Get current 2fa associated device
   */
  getAssociatedDevice(): Observable<TwoFADevice[]> {
    return this.http.get<TwoFADevice[]>(this.getResourceUrl()).pipe(map((res: TwoFADevice[]) => this.convertDateArrayFromServer(res)));
  }

  /**
   * Delete associated device
   * @param authReqId Authorization request id
   */
  deleteAssociatedDevice(authReqId: string): Observable<TwoFADevice> {
    const options = { headers: new HttpHeaders({ authPermission: authReqId }) };
    return this.http
      .delete<TwoFADevice>(this.getResourceUrl(), options)
      .pipe(map((res: TwoFADevice) => this.convertDateFromServer(res, 'activationTime')));
  }

  /**
   * Delete associated device with sms
   * @param authReqId Authorization request id
   */
  deleteAssociatedDeviceWithSms(authReqId: string, req: ContactRequest): Observable<TwoFADevice> {
    const options = { headers: new HttpHeaders({ authPermission: authReqId }) };
    return this.http
      .post<TwoFADevice>(this.getResourceUrl() + '/disactivate2fa/sms', req, options)
      .pipe(map((res: TwoFADevice) => this.convertDateFromServer(res, 'activationTime')));
  }

  /**
   * Utility function for convert date in string format to a Moment date for a specific field
   * @param res server response
   * @param key field to convert
   */
  protected convertDateFromServer<T>(res: T, key: keyof T) {
    if (res) {
      return {
        ...res,
        [key]: res[key] ? moment(res[key] as string) : undefined,
      };
    }
    return res;
  }

  /**
   * Utility function for convert date in string format to a Moment date for all date fields
   * @param res server response
   */
  protected convertDateArrayFromServer(res: TwoFADevice[]): TwoFADevice[] {
    if (res) {
      for (const a of res) {
        a.activationTime = moment(a.activationTime);
      }
    }
    return res;
  }
}
